/**
 * Pages a product grid a fixed number of cards at a time.
 *
 * The theme's own 'carousel' layout was not usable here: it wraps each product
 * card in a slideshow, and every product card already contains a slideshow for
 * its images. The nested components fight over their refs, so the outer arrows
 * do nothing, and the theme disables the card's image slideshow inside a
 * carousel, which leaves its arrows falling through to the product link.
 *
 * This keeps the plain grid (so the cards behave exactly as they do on the
 * collection and search pages) and shows one page of cards at a time, sliding
 * the incoming page in from the side the shopper is travelling towards.
 */
const STAGGER_MS = 45;

class MokyGridPager extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.section-resource-list') ?? this.closest('.shopify-section');
    if (!this.section) return;

    this.grid = this.section.querySelector('.resource-list--grid');
    if (!this.grid) return;

    this.items = Array.from(this.grid.children).filter((child) =>
      child.classList.contains('resource-list__item')
    );

    // One row per page: the desktop count on wide screens, the mobile count
    // (the grid's mobile columns) on phones. The grid is told how many
    // columns to use so the page always fills exactly one line.
    this.#mobileQuery = window.matchMedia('(max-width: 749px)');
    this.#applyPerPage();
    this.page = 0;

    this.prevButton = this.querySelector('[data-moky-pager-prev]');
    this.nextButton = this.querySelector('[data-moky-pager-next]');

    if (this.items.length === 0) {
      this.setAttribute('hidden', '');
      return;
    }

    this.prevButton?.addEventListener('click', this.#onPrev);
    this.nextButton?.addEventListener('click', this.#onNext);
    this.#mobileQuery.addEventListener('change', this.#onBreakpoint);

    this.#positionArrows();
    this.#resizeObserver = new ResizeObserver(() => this.#positionArrows());
    this.#resizeObserver.observe(this.grid);

    // First paint is not animated; there is nothing to travel from.
    this.#render(null);
  }

  disconnectedCallback() {
    this.prevButton?.removeEventListener('click', this.#onPrev);
    this.nextButton?.removeEventListener('click', this.#onNext);
    this.#mobileQuery?.removeEventListener('change', this.#onBreakpoint);
    this.#resizeObserver?.disconnect();
  }

  /** @type {MediaQueryList | undefined} */
  #mobileQuery;

  #applyPerPage() {
    const read = (value, fallback) => {
      const n = parseInt(value ?? '', 10);
      return Number.isFinite(n) && n > 0 ? n : fallback;
    };
    const desktop = read(this.dataset.perPage, 4);
    const mobile = read(this.dataset.perPageMobile, 2);
    this.perPage = this.#mobileQuery?.matches ? mobile : desktop;
    this.pageCount = Math.max(1, Math.ceil(this.items.length / this.perPage));
    this.section?.style.setProperty('--moky-pager-cols', String(this.perPage));
    // Nothing to page through: no arrows, and the grid keeps its full width
    // (the side lanes for the arrows are only reserved while paging).
    this.toggleAttribute('hidden', this.pageCount <= 1);
    this.section?.classList.toggle('moky-pager-host', this.pageCount > 1);
  }

  #onBreakpoint = () => {
    const first = this.page * this.perPage;
    this.#applyPerPage();
    this.page = Math.min(Math.floor(first / this.perPage), this.pageCount - 1);
    this.#render(null);
  };

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #onPrev = () => this.#goTo(this.page - 1);

  #onNext = () => this.#goTo(this.page + 1);

  #goTo(page) {
    const clamped = Math.min(Math.max(page, 0), this.pageCount - 1);
    if (clamped === this.page) return;

    const direction = clamped > this.page ? 'next' : 'previous';
    this.page = clamped;
    this.#render(direction);
  }

  /**
   * Puts the arrows at the same height as the small arrows on the card images:
   * centred on the card image, not on the whole card, so they do not drift down
   * as titles wrap to different numbers of lines.
   *
   * The offset is measured from the element the arrows are actually positioned
   * against. Using the grid's offsetTop assumed that element was the section,
   * which is only true when nothing between them is positioned -- when it was
   * not, the arrows sat at a different height from the card arrows.
   */
  #positionArrows() {
    const firstVisible = this.items[this.page * this.perPage] ?? this.items[0];
    if (!firstVisible) return;

    const gallery = firstVisible.querySelector('.card-gallery') ?? firstVisible;
    const base = this.prevButton?.offsetParent ?? this.section;
    if (!base) return;

    const galleryRect = gallery.getBoundingClientRect();
    const baseRect = base.getBoundingClientRect();
    if (galleryRect.height <= 0) return;

    const top = galleryRect.top + galleryRect.height / 2 - baseRect.top;
    this.section.style.setProperty('--moky-pager-top', `${top}px`);
  }

  /**
   * @param {'next'|'previous'|null} direction - Which way the page moved, or
   *   null for the initial render.
   */
  #render(direction) {
    const start = this.page * this.perPage;
    const end = start + this.perPage;
    const enterClass = direction === 'next' ? 'moky-pager__item--in-right' : 'moky-pager__item--in-left';

    this.items.forEach((item, index) => {
      const visible = index >= start && index < end;

      item.classList.remove('moky-pager__item--in-right', 'moky-pager__item--in-left');
      item.classList.toggle('moky-pager__item--hidden', !visible);
      // Keep hidden cards out of the tab order and off screen readers.
      item.inert = !visible;

      if (visible && direction) {
        item.style.setProperty('--moky-pager-delay', `${(index - start) * STAGGER_MS}ms`);
        // Force a reflow so the animation restarts even on a rapid second click.
        void item.offsetWidth;
        item.classList.add(enterClass);
      }
    });

    if (this.prevButton) this.prevButton.disabled = this.page === 0;
    if (this.nextButton) this.nextButton.disabled = this.page === this.pageCount - 1;

    this.#positionArrows();
  }
}

if (!customElements.get('moky-grid-pager')) {
  customElements.define('moky-grid-pager', MokyGridPager);
}
